import type { AnyObject } from "@entrilia/shared/types.ts";
import { getRowNode } from "@entrilia/shared/utilities/dataGridApiHelper.ts";
import type { GridApiPremium, GridBasicGroupNode, GridColDef } from "@mui/x-data-grid-premium";
import equal from "fast-deep-equal";
import type { RefObject } from "react";
import { BiAggregation, FormatType } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  GroupTotal,
  ReportField,
  TabularDataCell,
  TabularRow,
  ValueCell,
} from "../../../../../../../../../api/types/svTypes.ts";
import { isAggregation, isMeasure } from "../../../../../utilities/fieldsHelper.ts";
import { buildColumnBase } from "../base-column/buildColumnBase.ts";
import { buildTabularCell } from "../buildTabularCell.tsx";
import { getCellFromRow } from "../dimension-column/buildDimensionColumnHelper.ts";
import { getGroupKeys } from "../getGroupKeys.ts";
import { renderColumnHeader } from "../tabularGridDataProvider.tsx";

export const findGroupTotalCell = (
  rowNode: GridBasicGroupNode,
  fieldName: string,
  fields: ReportField[],
  grandTotals: AnyObject,
  groupTotals: GroupTotal[],
  apiRef: RefObject<GridApiPremium | null>
): TabularDataCell | undefined => {
  if (rowNode.parent === null && rowNode.groupingKey === null) {
    return grandTotals[fieldName] as TabularDataCell;
  }
  const storedGroup = apiRef.current?.getRow<TabularRow>(rowNode.id);
  if (storedGroup?.groupField) {
    return storedGroup.totals?.[fieldName];
  }
  const filters = getGroupKeys(rowNode, fields, apiRef);
  const group = groupTotals.find((gt) => hasSameFilters(gt.filters, filters));
  return group?.values[fieldName] as TabularDataCell | undefined;
};

type MeasureColumnOptions = {
  field: ReportField;
  getFieldTitle: (guid: string) => string;
  fields: RefObject<ReportField[]>;
  groupTotals: RefObject<GroupTotal[]>;
  grandTotals: RefObject<AnyObject>;
  apiRef: RefObject<GridApiPremium | null>;
  disableCellClick: boolean;
  isFailedToLoad: (fieldGuid: string) => boolean;
};

export const buildMeasureColumn = ({
  field,
  getFieldTitle,
  fields,
  grandTotals,
  groupTotals,
  apiRef,
  disableCellClick,
  isFailedToLoad,
}: MeasureColumnOptions): GridColDef => {
  return {
    ...buildColumnBase(
      {
        guid: field.config.guid,
        headerName: field.config.customLabel || field.config.systemLabel || field.meta.caption || "",
        hideAggregation: field.config.hideAggregation,
        align:
          (isAggregation(field) && field.config.aggregation === BiAggregation.StringAgg) ||
          field.config.format?.formatType === FormatType.None
            ? "left"
            : "right",
        groupable: isMeasure(field) && field.meta.capabilities.isScalar,
      },
      apiRef
    ),
    renderHeader: () => renderColumnHeader(getFieldTitle(field.config.guid), isFailedToLoad(field.config.guid)),
    renderCell: ({ id, row, aggregation, value, field: columnField }) => {
      let formattedValue = (value as TabularDataCell | undefined)?.formattedValue ?? "";
      let cell = getCellFromRow(row, columnField);
      let isGrandTotal = false;
      if (id) {
        let rowNode = getRowNode(apiRef, id);
        if (rowNode?.type === "footer") {
          rowNode = getRowNode(apiRef, rowNode.parent);
        }
        if (rowNode?.type === "pinnedRow") {
          rowNode = getRowNode(apiRef, rowNode.parent);
          cell = grandTotals.current[columnField] as TabularDataCell;
          isGrandTotal = true;
        }
        if (rowNode?.type === "group") {
          // if aggregation has undefined value it means it's need to be hidden
          if (field.config.hideAggregation === true || aggregation === undefined) {
            return "";
          }

          const groupTotalCell = findGroupTotalCell(
            rowNode,
            columnField,
            fields.current,
            grandTotals.current,
            groupTotals.current,
            apiRef
          );
          if (groupTotalCell) {
            cell = groupTotalCell;
          }
          formattedValue = groupTotalCell?.formattedValue ?? "";
        }
      }

      return buildTabularCell({
        cellType: "measure",
        cell,
        formattedValue,
        aggregation: !!aggregation,
        isGrandTotal,
        disableCellClick,
      });
    },
  };
};

const hasSameFilters = (filters: Record<string, ValueCell>, groupKeys: Record<string, Partial<ValueCell>>) => {
  if (filters === groupKeys) return true;
  if (filters === null || groupKeys === null) return false;
  if (Object.keys(filters).length !== Object.keys(groupKeys).length) return false;
  for (const key in groupKeys) {
    if (!equal(filters[key]?.value?.toString(), groupKeys[key]?.value?.toString())) {
      return false;
    }
  }
  return true;
};
